import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, UserPlus, X } from 'lucide-react';
import { membersApi, memberLabel, WorkspaceMember } from '@/lib/members';
import { cn } from '@/lib/cn';

/**
 * Reusable picker for choosing real workspace members instead of pasting
 * raw user UUIDs. Used by approval signer chains and ringi tiers (and any
 * future @mention / actor-filter surface).
 *
 * `ordered` mode renders step numbers and up/down reorder controls — the
 * order of `value` is the order signers must sign in. A selected id that
 * isn't in the member list (e.g. legacy seed data like "u1") is still shown
 * as a raw chip so existing approvals don't silently lose signers.
 */
export function MemberPicker({
  wsId,
  value,
  onChange,
  ordered = false,
  addLabel,
  emptyLabel,
  disabled,
  className,
}: {
  wsId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  ordered?: boolean;
  addLabel?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const addLbl = addLabel ?? t('common.add_member', { defaultValue: 'Add member…' });
  const emptyLbl = emptyLabel ?? t('common.no_members', { defaultValue: 'No members selected.' });
  const { data: members = [], isLoading } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => membersApi.list(wsId),
    enabled: !!wsId,
    staleTime: 5 * 60_000,
  });

  const byId = new Map<string, WorkspaceMember>(members.map((m) => [m.id, m]));
  const available = members.filter((m) => !value.includes(m.id));

  function add(id: string) {
    if (!id || value.includes(id)) return;
    onChange([...value, id]);
  }
  function remove(id: string) {
    onChange(value.filter((v) => v !== id));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }

  return (
    <div className={cn('space-y-2', className)}>
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyLbl}</p>
      ) : (
        <ul className="space-y-1.5">
          {value.map((id, i) => {
            const m = byId.get(id);
            return (
              <li key={id} className="flex items-center gap-2 rounded-md border border-border bg-white px-2 py-1.5">
                {ordered && <span className="num text-[11px] text-muted-foreground w-5 text-center shrink-0">{i + 1}</span>}
                {m?.pictureUrl ? (
                  <img src={m.pictureUrl} alt="" className="h-6 w-6 rounded object-cover shrink-0" />
                ) : (
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-foreground text-background text-[10px] font-semibold shrink-0">
                    {(m ? memberLabel(m) : id).trim().charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="flex-1 min-w-0 text-sm truncate">
                  {m ? (
                    <>
                      <span className="text-ink-800">{memberLabel(m)}</span>
                      {m.name && m.email && <span className="text-muted-foreground text-xs"> · {m.email}</span>}
                    </>
                  ) : (
                    <span className="num text-ink-500 text-xs" title={t('common.not_a_member', { defaultValue: 'Not a current member' })}>{id}</span>
                  )}
                </span>
                {ordered && (
                  <span className="flex items-center shrink-0">
                    <button type="button" disabled={disabled || i === 0} onClick={() => move(i, -1)}
                      className="p-1 text-ink-400 hover:text-ink-700 disabled:opacity-30" aria-label={t('common.move_up', { defaultValue: 'Move up' })}>
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" disabled={disabled || i === value.length - 1} onClick={() => move(i, 1)}
                      className="p-1 text-ink-400 hover:text-ink-700 disabled:opacity-30" aria-label={t('common.move_down', { defaultValue: 'Move down' })}>
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </span>
                )}
                <button type="button" disabled={disabled} onClick={() => remove(id)}
                  className="p-1 text-ink-400 hover:text-warn-600 shrink-0" aria-label={t('common.remove', { defaultValue: 'Remove' })}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
        <select
          value=""
          disabled={disabled || isLoading || available.length === 0}
          onChange={(e) => add(e.target.value)}
          className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm disabled:opacity-60"
        >
          <option value="" disabled>
            {isLoading ? t('common.loading_members', { defaultValue: 'Loading members…' }) : available.length === 0 ? t('common.all_members_added', { defaultValue: 'All members added' }) : addLbl}
          </option>
          {available.map((m) => (
            <option key={m.id} value={m.id}>
              {memberLabel(m)}{m.name && m.email ? ` · ${m.email}` : ''} ({m.role})
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
